import { ZfToggle } from '@zafire/react';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ClientPackageDto } from '../../api/generated/client';
import { ClientPackageChapterName } from '../../enums';
import { getUpdatedClientPackageChapter, getUpdatedClientPackageChapterWithDetails } from '../../utils/clientPackageUtils';
import { ClientPackageCheckboxItem } from './ClientPackageCheckboxItem';

interface IProps {
    clientPackage: ClientPackageDto;
    disabled?: boolean;
    updateClientPackage: (updatedPackage: Partial<ClientPackageDto>) => void;
}

export const ClientPackageChapterSelector: FunctionComponent<IProps> = ({ clientPackage, disabled, updateClientPackage }) => {
    const { t } = useTranslation();

    const onChapterSelected = useCallback(
        (chapterName: string, selected: boolean) => {
            updateClientPackage(getUpdatedClientPackageChapter(clientPackage, chapterName, selected));
        },
        [clientPackage, updateClientPackage],
    );

    const onWithDetailsChanged = useCallback(
        (chapterName: string, withDetails: boolean) => {
            updateClientPackage(getUpdatedClientPackageChapterWithDetails(clientPackage, chapterName, withDetails));
        },
        [clientPackage, updateClientPackage],
    );

    return (
        <div className="client-package-editor__selector-panel">
            <ZfToggle
                label={t('clientPackagePagesMasterToggle')}
                disabled={disabled}
                checked={clientPackage.chaptersEnabled}
                onToggleChange={(event: CustomEvent<boolean>) => updateClientPackage({ chaptersEnabled: event.detail })}
            />
            <div className="client-package-editor__selector-panel__content">
                {clientPackage.chaptersEnabled && (
                    <>
                        {[...clientPackage.clientPackageChapters]
                            .sort((a, b) => a.position - b.position)
                            .map(({ chapterName, selected, withDetails }) => {
                                const isLiquidityChapter = chapterName.toString() === ClientPackageChapterName.LIQUIDITY;

                                return (
                                    <div key={chapterName.toString()} className="client-package-editor__selector-panel__chapter-item">
                                        <ClientPackageCheckboxItem
                                            label={t(`clientPackagePage-${chapterName.toString()}`)}
                                            isDisabled={disabled || !clientPackage.chaptersEnabled}
                                            isSelected={selected}
                                            onChange={(isChecked) => onChapterSelected(chapterName, isChecked)}
                                        />
                                        {isLiquidityChapter && selected && (
                                            <div className="client-package-editor__selector-panel__sub-item">
                                                <ClientPackageCheckboxItem
                                                    label={t('clientPackagePage-LIQUIDITY-withDetails')}
                                                    isDisabled={disabled || !clientPackage.chaptersEnabled}
                                                    isSelected={withDetails ?? true}
                                                    onChange={(isChecked) => onWithDetailsChanged(chapterName, isChecked)}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                    </>
                )}
            </div>
        </div>
    );
};
